import AdditionalAccounts from '../../teams/detailCards/AdditionalAccounts';
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { doc, getDoc } from 'firebase/firestore';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase-client';
import SubmitButton from '../formComponents/SubmitButton';
import ErrorMessage from '../formComponents/ErrorMessage';
import TagSelector from '../formComponents/TagSelector';
import { useAuth } from '../../../auth/AuthContext';
import { createNotification } from '../../../utils/createNotification';
import { getFunctionUrl } from '../../../utils/functionURLs';
import { getMembershipPlanLabel, isFamilyMembershipMember } from '../../../utils/membershipReporting';

function normalizeId(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeIdList(value) {
  return Array.isArray(value)
    ? Array.from(new Set(value.map(normalizeId).filter(Boolean)))
    : [];
}

function cleanDisplayName(value) {
  const name = typeof value === 'string' ? value.trim() : '';
  if (!name) return '';
  return /^unknown (member|payer)$/i.test(name) ? '' : name;
}

function getFamilyRoleIds(member = {}) {
  const roles = member?.familyRoles && typeof member.familyRoles === 'object' ? member.familyRoles : {};
  return Object.keys(roles).map(normalizeId).filter(Boolean);
}

function getFamilyRole(member = {}, familyId = '') {
  const normalizedFamilyId = normalizeId(familyId);
  const roles = member?.familyRoles && typeof member.familyRoles === 'object' ? member.familyRoles : {};
  return normalizedFamilyId ? normalizeId(roles[normalizedFamilyId]).toLowerCase() : '';
}

function inferVisibleFamilyOwnerId(memberProfiles = [], familyId = '') {
  const normalizedFamilyId = normalizeId(familyId);
  if (!normalizedFamilyId) return '';

  const candidates = (memberProfiles || [])
    .map((profile) => {
      const id = normalizeId(profile?.id);
      if (!id || !getFamilyRoleIds(profile).includes(normalizedFamilyId)) return null;
      const role = getFamilyRole(profile, normalizedFamilyId);
      const isCovered = normalizeId(profile.membershipCoveredByUid)
        && normalizeId(profile.membershipCoveredByUid) !== id;
      const isPaymentOwner = normalizeId(profile.membershipPaymentOwnerUid) === id;
      const isFamilyPlanOwner = normalizeId(profile.membershipPlanType).toLowerCase() === 'family_tier';
      const roleScore = role === 'parent' || role === 'guardian' || role === 'adult'
        ? 4
        : role === 'child'
          ? 0
          : 2;
      const coverageScore = isFamilyPlanOwner || isPaymentOwner
        ? 4
        : isCovered
          ? 0
          : 2;
      return {
        id,
        name: cleanDisplayName(profile.name) || id,
        score: roleScore + coverageScore,
      };
    })
    .filter(Boolean)
    .sort((left, right) => right.score - left.score || left.name.localeCompare(right.name));

  return candidates[0]?.id || '';
}

function getStoredPlanLabel(member = {}) {
  const direct = typeof member.membershipPlanLabel === 'string' ? member.membershipPlanLabel.trim() : '';
  const snapshotLabel = typeof member.membershipPlanSnapshot?.label === 'string'
    ? member.membershipPlanSnapshot.label.trim()
    : '';
  return snapshotLabel || direct;
}

function getPlanLabel(member = {}) {
  return getMembershipPlanLabel(member);
}

function getCoverageKind(member = {}) {
  if (member.membershipCoverageType === 'additional') return 'Included';
  if (!isFamilyMembershipMember(member)) return 'Individual';
  const memberId = normalizeId(member.id);
  const coveredByUid = normalizeId(member.membershipCoveredByUid);
  const paymentOwnerUid = normalizeId(member.membershipPaymentOwnerUid);
  const coveredIds = normalizeIdList(member.membershipCoveredMemberIds);
  const coverageType = normalizeId(member.membershipCoverageType).toLowerCase();
  const planType = normalizeId(member.membershipPlanType).toLowerCase();

  if (coveredByUid && coveredByUid !== memberId) return 'Family';
  if (
    coverageType === 'family'
    || planType === 'family_tier'
    || coveredIds.length > 1
    || (paymentOwnerUid && paymentOwnerUid === memberId && coveredIds.length > 0)
  ) {
    return 'Family';
  }
  return 'Individual';
}

export default function EditMemberForm({
  teamId,
  member,
  allTags = [],
  adminRoles = [],
  adminAssignments = [],
  memberProfiles = [],
  onClose,
  onSave,
  teamName
}) {
  const {
    user
  } = useAuth();
  const [tags, setTags] = useState({});
  const [tagsDirty, setTagsDirty] = useState(false);
  const [initialTagsFlat, setInitialTagsFlat] = useState({});
  const [isAdmin, setIsAdmin] = useState(false); // UI state for the target member
  const [initialIsAdmin, setInitialIsAdmin] = useState(false); // snapshot from team doc
  const [limitedRoleId, setLimitedRoleId] = useState('');
  const [initialLimitedRoleId, setInitialLimitedRoleId] = useState('');
  const [canManageIncludedAccounts, setCanManageIncludedAccounts] = useState(false);
  const [teamAdmins, setTeamAdmins] = useState([]); // full admin list for guards
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [membershipRequest, setMembershipRequest] = useState(null);
  const [membershipDetails, setMembershipDetails] = useState(member || {});
  const [requestActionLoading, setRequestActionLoading] = useState(false);
  const [requestActionStatus, setRequestActionStatus] = useState('');
  const editingSelf = user?.uid === member?.id;
  const canManageRoles = teamAdmins.includes(user?.uid); // viewer is an admin

  const normalizeMemberTags = useCallback(memberTags => {
    if (Array.isArray(memberTags)) {
      return memberTags.reduce((acc, t) => {
        if (t) acc[t] = true;
        return acc;
      }, {});
    }
    return memberTags && typeof memberTags === 'object' ? {
      ...memberTags
    } : {};
  }, []);
  const buildSelectedTags = useCallback(memberTags => {
    const flat = normalizeMemberTags(memberTags);
    const grouped = {};
    (allTags || []).forEach(t => {
      if (!t?.name) return;
      if (!flat[t.name]) return;
      const cat = t.category || 'Uncategorised';
      grouped[cat] = grouped[cat] || [];
      grouped[cat].push(t.name);
    });
    return grouped;
  }, [allTags, normalizeMemberTags]);
  const flattenSelectedTags = selected => {
    const out = {};
    Object.values(selected || {}).forEach(arr => {
      (arr || []).forEach(t => {
        if (t) out[t] = true;
      });
    });
    return out;
  };

  // Load member tags when the row changes
  useEffect(() => {
    if (member) {
      setMembershipDetails(member);
      setTagsDirty(false);
      const fromMember = buildSelectedTags(member.tags);
      setTags(fromMember);
      setInitialTagsFlat(normalizeMemberTags(member.tags));
    }
  }, [member, buildSelectedTags, normalizeMemberTags]);

  // Fetch tags from the member doc (memberProfiles does not include tags)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!teamId || !member?.id) return;
      try {
        const memberRef = doc(db, 'teams', teamId, 'members', member.id);
        const snap = await getDoc(memberRef);
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const flat = normalizeMemberTags(data.tags);
        if (cancelled) return;
        setMembershipDetails({
          ...member,
          ...data,
          id: member.id,
          name: member.name || data.name || member.id
        });
        setInitialTagsFlat(flat);
        if (!tagsDirty) setTags(buildSelectedTags(flat));
      } catch (e) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, member, member?.id, tagsDirty, buildSelectedTags, normalizeMemberTags]);

  // Load admin truth from team doc
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!teamId || !member?.id) return;
      setLoadingAdmins(true);
      try {
        const teamRef = doc(db, 'teams', teamId);
        const snap = await getDoc(teamRef);
        const admins = snap.data()?.admins || [];
        if (!mounted) return;
        setTeamAdmins(admins);
        const assignment = await getDoc(doc(db, 'teams', teamId, 'adminAssignments', user?.uid));
        if (mounted) setCanManageIncludedAccounts(admins.includes(user?.uid) || (assignment.data()?.permissionKeys || []).includes('membership.manage'));
        const memberIsAdmin = admins.includes(member.id);
        setInitialIsAdmin(memberIsAdmin);
        setIsAdmin(memberIsAdmin); // seed UI from truth, not member.role
      } catch (e) {
        if (mounted) setError('Failed to load team permissions.');
      } finally {
        if (mounted) setLoadingAdmins(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [teamId, member?.id, user?.uid]);
  useEffect(() => {
    if (!member?.id) return;
    const assignment = (adminAssignments || []).find((entry) => (entry.uid || entry.id) === member.id);
    const nextRoleId = assignment?.roleId || '';
    setLimitedRoleId(nextRoleId);
    setInitialLimitedRoleId(nextRoleId);
  }, [adminAssignments, member?.id]);
  useEffect(() => {
    setRequestActionStatus('');
    if (!teamId || !member?.id || !canManageRoles) {
      setMembershipRequest(null);
      return undefined;
    }
    const requestRef = doc(db, 'teams', teamId, 'membershipRequests', member.id);
    const unsub = safeOnSnapshot(requestRef, snap => {
      const data = snap.exists() ? snap.data() || {} : null;
      const status = String(data?.status || '').trim().toLowerCase();
      if (!data || (status && status !== 'requested' && status !== 'pending')) {
        setMembershipRequest(null);
        return;
      }
      setMembershipRequest({
        id: snap.id,
        ...data
      });
    }, err => {
      if (String(err?.code || '') !== 'permission-denied') {}
      setMembershipRequest(null);
    });
    return () => unsub();
  }, [teamId, member?.id, canManageRoles]);
  const formatTimestamp = value => {
    const date = typeof value?.toDate === 'function' ? value.toDate() : value instanceof Date ? value : null;
    return date ? date.toLocaleString() : '';
  };
  const updateMembershipRequestStatus = async status => {
    if (!teamId || !member?.id || !membershipRequest) return;
    setRequestActionLoading(true);
    setRequestActionStatus('');
    try {
      const token = await user.getIdToken(true);
      const res = await fetch(getFunctionUrl('approveMembershipChange'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          teamId,
          memberId: member.id,
          action: status === 'resolved' ? 'approve' : 'decline'
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to review membership change.');
      }
      if (status === 'resolved') {
        if (data?.status === 'approved_pending_payment' || data?.status === 'approved_pending_payment_setup') {
          setRequestActionStatus('Approved. Member now needs to pay to activate the change.');
        } else if (data?.status === 'scheduled_for_renewal') {
          setRequestActionStatus('Approved and scheduled for the next renewal.');
        } else {
          setRequestActionStatus('Approved and applied.');
        }
      } else {
        setRequestActionStatus('Request declined.');
      }
    } catch (err) {
      setRequestActionStatus(err?.message || 'Failed to update the request.');
    } finally {
      setRequestActionLoading(false);
    }
  };
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!teamId || !member?.id) {
        throw new Error('Invalid team or member.');
      }
      const flatTags = flattenSelectedTags(tags);
      if (canManageRoles) {
        const updateTeamMemberTagsFn = httpsCallable(functions, 'updateTeamMemberTags');
        await updateTeamMemberTagsFn({
          teamId,
          memberId: member.id,
          selectedTags: Object.keys(flatTags).filter(tagName => flatTags[tagName])
        });
      }

      // Only touch roles/admins if editing someone else AND viewer is an admin
      if (!editingSelf && canManageRoles) {
        if (isAdmin !== initialIsAdmin) {
          const setAdminFn = httpsCallable(functions, 'setTeamMemberAdminStatus');
          await setAdminFn({
            teamId,
            memberId: member.id,
            isAdmin
          });
        }
        if (!isAdmin && limitedRoleId !== initialLimitedRoleId) {
          const assignRoleFn = httpsCallable(functions, 'assignAdminRole');
          await assignRoleFn({
            teamId,
            memberId: member.id,
            roleId: limitedRoleId
          });
        }
      }

      // Build change summary based on initial vs new
      const roleChanged = !editingSelf && canManageRoles && isAdmin !== initialIsAdmin;
      const limitedRoleChanged = !editingSelf && canManageRoles && !isAdmin && limitedRoleId !== initialLimitedRoleId;
      const tagsChanged = JSON.stringify(flatTags) !== JSON.stringify(initialTagsFlat);
      const changes = [];
      if (roleChanged) changes.push(isAdmin ? 'granted admin access' : 'removed as admin');
      if (limitedRoleChanged) changes.push(limitedRoleId ? 'updated admin permissions' : 'removed admin permissions');
      if (tagsChanged) changes.push('updated tags');
      if (changes.length > 0) {
        await createNotification({
          userId: member.id,
          message: `Your membership in ${teamName} has been ${changes.join(' and ')}.`,
          actionUrl: `/teams/${teamId}`,
          teamId,
          notificationCategory: 'admin',
          pushContext: teamName,
        });
      }
      onSave?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to update member');
    } finally {
      setSaving(false);
    }
  };
  const handleKick = async () => {
    if (!teamId || !member?.id) return;
    if (member.id === user?.uid) {
      alert("You can't kick yourself.");
      return;
    }

    // Prevent kicking the last admin
    if (teamAdmins.length === 1 && teamAdmins[0] === member.id) {
      alert('Cannot remove the last admin of the team.');
      return;
    }
    const confirmed = window.confirm(`Are you sure you want to remove ${member.name || 'this member'} from the team?`);
    if (!confirmed) return;
    try {
      const kickFn = httpsCallable(functions, 'leaveTeamCallable');
      await kickFn({
        teamId,
        userId: member.id
      });
      alert(`${member.name || 'Member'} has been removed from the team.`);
      onSave?.();
      onClose?.();
    } catch (err) {
      alert('Failed to remove member. Please try again.');
    }
  };
  const memberNameById = new Map(
    (memberProfiles || []).map(profile => [
      profile.id,
      cleanDisplayName(profile.name) || profile.id
    ])
  );
  const coverageKind = getCoverageKind(membershipDetails);
  const coveredIds = normalizeIdList(membershipDetails.membershipCoveredMemberIds);
  const rawOwnerId = normalizeId(membershipDetails.membershipCoveredByUid)
    || normalizeId(membershipDetails.membershipPaymentOwnerUid);
  const familyId = normalizeId(membershipDetails.membershipCoverageFamilyId)
    || normalizeId(membershipDetails.membershipCoveredByFamilyId);
  const inferredOwnerId = rawOwnerId && memberNameById.get(rawOwnerId)
    ? ''
    : inferVisibleFamilyOwnerId(memberProfiles, familyId);
  const ownerId = inferredOwnerId || rawOwnerId;
  const displayedCoveredIds = new Set(coveredIds);
  if (coverageKind === 'Family' && ownerId) displayedCoveredIds.add(ownerId);
  const ownerName = ownerId ? (memberNameById.get(ownerId) || 'Membership payer') : '';
  const coveredNames = Array.from(displayedCoveredIds)
    .map(uid => memberNameById.get(uid))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  const planLabel = getPlanLabel(membershipDetails);
  const storedPlanLabel = getStoredPlanLabel(membershipDetails);
  return <>
      <h2>Edit Member</h2>

      {error && <ErrorMessage message={error} />}

      <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      padding: '0.75rem',
      background: '#f8fafc',
      marginBottom: 12
    }}>
        <strong>Membership coverage</strong>
        <p className="text-muted" style={{ margin: '0.5rem 0 0' }}>
          Status: {coverageKind}.
        </p>
        <p className="text-muted" style={{ margin: '0.25rem 0 0' }}>
          Plan: {planLabel}.
        </p>
        {storedPlanLabel && storedPlanLabel !== planLabel && <p className="text-muted" style={{ margin: '0.25rem 0 0' }}>
            Stored plan: {storedPlanLabel}.
          </p>}
        {coverageKind === 'Family' && ownerName && ownerId !== member?.id && <p className="text-muted" style={{ margin: '0.25rem 0 0' }}>
            Paid by: {ownerName}.
          </p>}
        {coverageKind === 'Family' && coveredNames.length > 0 && <p className="text-muted" style={{ margin: '0.25rem 0 0' }}>
            Covered members: {coveredNames.join(', ')}.
          </p>}
        {coverageKind !== 'Individual' && familyId && <p className="text-muted" style={{ margin: '0.25rem 0 0' }}>
            Family: Family membership.
          </p>}
      </div>

      {canManageIncludedAccounts && <AdditionalAccounts teamId={teamId} sponsorUid={member.id} />}

      {membershipRequest && canManageRoles && <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      padding: '0.75rem',
      background: '#f8fafc',
      marginBottom: 12
    }}>
          <strong>Pending membership change request</strong>
          <p style={{
        margin: '0.5rem 0'
      }}>
            {membershipRequest.message || 'No details provided.'}
          </p>

          {membershipRequest.requestedPlanLabel && <p className="text-muted" style={{
        margin: '0 0 0.5rem'
      }}>
              Requested plan: {membershipRequest.requestedPlanLabel}.
            </p>}

          {membershipRequest.requestedPricingTag && <p className="text-muted" style={{
        margin: '0 0 0.5rem'
      }}>
              Requested pricing option: {membershipRequest.requestedPricingTag}.
            </p>}

          {Array.isArray(membershipRequest.requestedAdminTagNames) && membershipRequest.requestedAdminTagNames.length > 0 && <p className="text-muted" style={{
        margin: '0 0 0.5rem'
      }}>
              Requested tags: {membershipRequest.requestedAdminTagNames.join(', ')}.
            </p>}

          {membershipRequest.requestedFamilyName && <p className="text-muted" style={{
        margin: '0 0 0.5rem'
      }}>
              Family: {membershipRequest.requestedFamilyName}.
            </p>}

          {Array.isArray(membershipRequest.requestedTargetDisplayNames) && membershipRequest.requestedTargetDisplayNames.length > 0 && <p className="text-muted" style={{
        margin: '0 0 0.5rem'
      }}>
              Requested coverage: {membershipRequest.requestedTargetDisplayNames.join(', ')}.
            </p>}

          {Array.isArray(membershipRequest.currentAdminTagNames) && membershipRequest.currentAdminTagNames.length > 0 && <p className="text-muted" style={{
        margin: '0 0 0.5rem'
      }}>
              Current admin-managed tags: {membershipRequest.currentAdminTagNames.join(', ')}.
            </p>}

          {formatTimestamp(membershipRequest.updatedAt || membershipRequest.createdAt) && <p className="text-muted" style={{
        margin: '0 0 0.75rem'
      }}>
              Requested {formatTimestamp(membershipRequest.updatedAt || membershipRequest.createdAt)}.
            </p>}

          <div style={{
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap'
      }}>
            <button type="button" className="btn btn--secondary btn--xs" onClick={() => updateMembershipRequestStatus('resolved')} disabled={requestActionLoading}>
              {requestActionLoading ? 'Saving...' : 'Approve'}
            </button>
            <button type="button" className="btn btn--danger btn--xs" onClick={() => updateMembershipRequestStatus('declined')} disabled={requestActionLoading}>
              Decline
            </button>
          </div>

          {requestActionStatus && <p className="text-muted" style={{
        margin: '0.75rem 0 0'
      }}>
              {requestActionStatus}
            </p>}
        </div>}

      {/* Admin toggle: hidden for self, disabled if viewer isn’t an admin */}
      {!loadingAdmins && user?.uid !== member?.id && <label style={{
      display: 'block',
      marginBottom: 12
    }}>
          <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} disabled={!canManageRoles} />{' '}
          Admin
          {!canManageRoles && <span style={{
        marginLeft: 8,
        opacity: 0.7
      }}>
              (You don’t have permission to change roles)
            </span>}
        </label>}

      {!loadingAdmins && user?.uid !== member?.id && canManageRoles && !isAdmin && <label style={{
      display: 'block',
      marginBottom: 12
    }}>
          <span style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Limited admin role</span>
          <select className="form-input w-full" value={limitedRoleId} onChange={e => setLimitedRoleId(e.target.value)}>
            <option value="">No limited admin role</option>
            {(adminRoles || []).map(role => <option key={role.id} value={role.id}>{role.name || 'Unnamed role'}</option>)}
          </select>
        </label>}

      <TagSelector allTags={allTags} selectedTags={tags} onChange={next => {
      setTagsDirty(true);
      setTags(next);
    }} />

      <div className="form-actions" style={{
      marginTop: 16,
      display: 'flex',
      gap: 8
    }}>
  <SubmitButton onClick={handleSave} label={saving ? 'Saving…' : 'Save'} disabled={saving || loadingAdmins} variant="primary" />

  <SubmitButton onClick={handleKick} label="Kick Member" disabled={loadingAdmins} variant="danger" // ← uses the new destructive style
      />

  <SubmitButton onClick={onClose} label="Cancel" variant="ghost" size="sm" />
    </div>

    </>;
}
EditMemberForm.propTypes = {
  teamId: PropTypes.string.isRequired,
  member: PropTypes.object.isRequired,
  allTags: PropTypes.array,
  adminRoles: PropTypes.array,
  adminAssignments: PropTypes.array,
  memberProfiles: PropTypes.array,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  teamName: PropTypes.string.isRequired
};
